class CardQuickadd {
  constructor() {
    this._selectors = {
      cardQuickadd: '[js-card-quickadd]',
      quickAddTrigger: '[js-quick-add-trigger]',
      quickAddOption: '[js-card-quickadd-option]',
      quickAddDrawer: '[js-card-quickadd-drawer]',
      cardContent: '.card__content',
      cardInformation: '.card__information',
      cardInformationFallback: '.card-information',
      quickAddProductForm: '[js-card-quickadd-product-form]',
      variantIdInput: 'input[name="id"]',
      productFormElement: 'product-form',
    };
  }

  init() {
    this._setListeners();

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this._syncAll);
    } else {
      this._syncAll();
    }

    window.addEventListener('resize', this._syncAll);
  }

  _setListeners() {
    document.addEventListener('click', this._onDocumentClick);
    document.addEventListener('product-form:cart-success', this._onCartSuccess);
  }

  _getCardContent(quickaddRoot) {
    return quickaddRoot ? quickaddRoot.closest(this._selectors.cardContent) : null;
  }

  _getDrawer(quickaddRoot) {
    if (!quickaddRoot) return null;

    const trigger = quickaddRoot.querySelector(this._selectors.quickAddTrigger);
    const controlsId = trigger ? trigger.getAttribute('aria-controls') : null;

    if (controlsId) {
      const controlledDrawer = document.getElementById(controlsId);
      if (controlledDrawer) return controlledDrawer;
    }

    const cardContent = this._getCardContent(quickaddRoot);
    return cardContent ? cardContent.querySelector(this._selectors.quickAddDrawer) : null;
  }

  _getQuickaddRootFromOption(element) {
    const cardContent = element ? element.closest(this._selectors.cardContent) : null;
    return cardContent ? cardContent.querySelector(this._selectors.cardQuickadd) : null;
  }

  _forEachOptionButton(quickaddRoot, callback) {
    const drawer = this._getDrawer(quickaddRoot);
    if (!drawer) return;
    drawer.querySelectorAll(this._selectors.quickAddOption).forEach(callback);
  }

  _syncQuickaddMetrics(quickaddRoot) {
    if (!quickaddRoot) return;

    const drawer = this._getDrawer(quickaddRoot);
    if (!drawer) return;

    const cardContent = this._getCardContent(quickaddRoot);
    if (!cardContent) return;

    const informationBlock =
      cardContent.querySelector(this._selectors.cardInformation) ||
      cardContent.querySelector(this._selectors.cardInformationFallback);

    let infoHeight = 0;

    if (informationBlock) {
      infoHeight = Math.max(
        informationBlock.getBoundingClientRect().height || 0,
        informationBlock.offsetHeight || 0,
        informationBlock.scrollHeight || 0
      );
    }

    const contentHeight = Math.max(
      cardContent.getBoundingClientRect().height || 0,
      cardContent.offsetHeight || 0,
      cardContent.scrollHeight || 0
    );

    let offset = infoHeight || contentHeight;

    cardContent.style.setProperty('--card-quickadd-offset', `${offset}px`);
    cardContent.style.setProperty('--card-quickadd-height', `${drawer.scrollHeight}px`);

    if (!offset) {
      requestAnimationFrame(() => {
        const retryOffset = Math.max(
          cardContent.getBoundingClientRect().height || 0,
          cardContent.offsetHeight || 0,
          cardContent.scrollHeight || 0
        );
        cardContent.style.setProperty('--card-quickadd-offset', `${retryOffset}px`);
      });
    }
  }

  _resetQuickadd(quickaddRoot) {
    this._syncQuickaddMetrics(quickaddRoot);

    quickaddRoot.dataset.quickaddOpen = 'false';
    quickaddRoot.dataset.quickaddSubmitting = 'false';
    quickaddRoot.classList.remove('is-open', 'card-quickadd--variant-selected');
    this._getCardContent(quickaddRoot)?.classList.remove('card-quickadd-open');

    const trigger = quickaddRoot.querySelector(this._selectors.quickAddTrigger);
    const productForm = quickaddRoot.querySelector(this._selectors.quickAddProductForm);
    const variantIdInput = quickaddRoot.querySelector(this._selectors.variantIdInput);
    const isSingleVariant = quickaddRoot.dataset.quickaddSingleVariant === 'true';

    if (trigger) {
      trigger.classList.remove('hidden');
      trigger.setAttribute('aria-expanded', 'false');
    }

    if (productForm) {
      productForm.classList.add('hidden');
    }

    this._forEachOptionButton(quickaddRoot, (optionButton) => {
      optionButton.classList.remove('is-selected');
    });

    if (variantIdInput && !isSingleVariant) {
      variantIdInput.value = '';
      variantIdInput.disabled = true;
    }
  }

  _submitQuickadd(quickaddRoot) {
    const productForm = quickaddRoot?.querySelector(this._selectors.quickAddProductForm);
    const form = productForm?.querySelector('form');
    const variantIdInput = quickaddRoot?.querySelector(this._selectors.variantIdInput);

    if (!form || !variantIdInput?.value) {
      return;
    }

    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }
  }

  _openDrawer(quickaddRoot) {
    this._syncQuickaddMetrics(quickaddRoot);

    quickaddRoot.dataset.quickaddOpen = 'true';
    quickaddRoot.classList.add('is-open');
    this._getCardContent(quickaddRoot)?.classList.add('card-quickadd-open');

    const trigger = quickaddRoot.querySelector(this._selectors.quickAddTrigger);
    const productForm = quickaddRoot.querySelector(this._selectors.quickAddProductForm);
    const isSingleVariant = quickaddRoot.dataset.quickaddSingleVariant === 'true';

    if (trigger) {
      trigger.setAttribute('aria-expanded', 'true');
    }

    if (isSingleVariant && productForm) {
      productForm.classList.remove('hidden');
      if (trigger) {
        trigger.classList.add('hidden');
      }
      this._submitQuickadd(quickaddRoot);
    }
  }

  _onDocumentClick = (event) => {
    const trigger = event.target.closest(this._selectors.quickAddTrigger);

    if (trigger) {
      const quickaddRoot = trigger.closest(this._selectors.cardQuickadd);
      if (!quickaddRoot) return;

      const isSingleVariant = quickaddRoot.dataset.quickaddSingleVariant === 'true';
      const alreadyOpen = quickaddRoot.classList.contains('is-open');

      if (alreadyOpen && !isSingleVariant && !quickaddRoot.classList.contains('card-quickadd--variant-selected')) {
        this._resetQuickadd(quickaddRoot);
        return;
      }

      if (!alreadyOpen) {
        this._openDrawer(quickaddRoot);
      }

      return;
    }

    const optionButton = event.target.closest(this._selectors.quickAddOption);

    if (!optionButton || optionButton.disabled) return;

    const quickaddRoot = this._getQuickaddRootFromOption(optionButton);
    if (!quickaddRoot) return;

    const variantId = optionButton.dataset.variantId;
    if (!variantId) return;

    this._forEachOptionButton(quickaddRoot, (otherOption) => {
      otherOption.classList.remove('is-selected');
    });
    optionButton.classList.add('is-selected');

    const variantIdInput = quickaddRoot.querySelector(this._selectors.variantIdInput);
    if (variantIdInput) {
      variantIdInput.value = variantId;
      variantIdInput.disabled = false;
    }

    const productForm = quickaddRoot.querySelector(this._selectors.quickAddProductForm);
    const quickAddTrigger = quickaddRoot.querySelector(this._selectors.quickAddTrigger);

    if (productForm) {
      productForm.classList.remove('hidden');
    }

    if (quickAddTrigger) {
      quickAddTrigger.classList.add('hidden');
    }

    quickaddRoot.classList.add('card-quickadd--variant-selected');
    this._submitQuickadd(quickaddRoot);
  };

  _onCartSuccess = (event) => {
    const productFormNode =
      event.target && event.target.closest && event.target.closest(this._selectors.productFormElement);
    const quickaddRoot = productFormNode && productFormNode.closest(this._selectors.cardQuickadd);

    if (quickaddRoot) {
      this._resetQuickadd(quickaddRoot);
    }
  };

  _syncAll = () => {
    document.querySelectorAll(this._selectors.cardQuickadd).forEach((quickaddRoot) => {
      const cardContent = this._getCardContent(quickaddRoot);
      if (cardContent) {
        cardContent.classList.add('card-quickadd-context');
      }
      this._syncQuickaddMetrics(quickaddRoot);
    });
  };
}

new CardQuickadd().init();
